import { User } from './user';
import { Status } from './status';
import { Aircraft } from './aircraft';
import { LegRequest } from './leg-request';
import { CurrencyEnum } from '../../../../shared/models';
import { PriceTypeEnum } from './price-type-enum';
import { OfferTypeEnum } from './offer-type-enum';
import { AircraftCategory } from './aircraft-category';
import { CreationTypeEnum } from './creation-type-enum';
import { LegAirportChanges } from './leg-airport-changes';
import { OfferBidStatusEnum } from './offer-bid-status-enum';

export class OperatorPurchaseOffer {
  sourcingRequestId: string;
  parentSourcingRequestId: string = null;
  offerPrice: number = null;
  createdUser: User;
  aircraftId: number;
  aircraftCategory: AircraftCategory;
  aircraftCategories: Array<AircraftCategory>;
  aircraftCategoryIds: Array<number>;
  lastModifiedDate: Date;
  lastModifiedUser: User;
  createdDate: Date;
  expirationDate: Date;
  legs: Array<LegRequest>;
  purchaseOfferId: string;
  operatorPurchaseOfferId: string;
  status: Status;
  acceptedDate?: Date;
  acceptedByUser?: User;
  cancelledDate?: Date;
  cancelledBy?: User;
  priceType: PriceTypeEnum = null;
  type: OfferTypeEnum = null;
  flightPrice: number;
  offerStatus?: OfferBidStatusEnum = null;
  aircraft?: Aircraft = null;
  currency: CurrencyEnum = null;
  offerPriceInCurrency: number = null;
  currencyUpdateTime?: Date = null;
  currencyRate?: number = null;
  legAirportChanges: Array<LegAirportChanges> = null;
  firmFlexDeparture: boolean = null;
  firmFlexDepartureFee: number = null;
  creationType: CreationTypeEnum;
  clientBillingCurrency: CurrencyEnum; //mapped from SR.clientDetails.billingCurrency

  getClientBillingCurrency(): string {
    return this.clientBillingCurrency;
  }

  setClientBillingCurrency(clientBillingCurrency: CurrencyEnum): OperatorPurchaseOffer {
    this.clientBillingCurrency = clientBillingCurrency;
    return this;
  }

  getPurchaseOfferId(): string {
    return this.purchaseOfferId;
  }

  setPurchaseOfferId(purchaseOfferId: string): OperatorPurchaseOffer {
    this.purchaseOfferId = purchaseOfferId;
    return this;
  }

  getExpirationDate(): Date {
    return this.expirationDate;
  }

  setExpirationDate(expirationDate: Date): OperatorPurchaseOffer {
    this.expirationDate = expirationDate;
    return this;
  }

  getOperatorPurchaseOfferId(): string {
    return this.operatorPurchaseOfferId;
  }

  setOperatorPurchaseOfferId(operatorPurchaseOfferId: string): OperatorPurchaseOffer {
    this.operatorPurchaseOfferId = operatorPurchaseOfferId;
    return this;
  }

  getSourcingRequestId(): string {
    return this.sourcingRequestId;
  }

  setSourcingRequestId(sourcingRequestId: string): OperatorPurchaseOffer {
    this.sourcingRequestId = sourcingRequestId;
    return this;
  }

  getParentSourcingRequestId(): string {
    return this.parentSourcingRequestId;
  }

  setParentSourcingRequestId(parentSourcingRequestId: string): OperatorPurchaseOffer {
    this.parentSourcingRequestId = parentSourcingRequestId;
    return this;
  }

  getAircraftCategory(): AircraftCategory {
    return this.aircraftCategory;
  }

  setAircraftCategory(aircraftCategory: AircraftCategory): OperatorPurchaseOffer {
    this.aircraftCategory = aircraftCategory;

    return this;
  }

  getAircraftCategoryIds(): Array<number> {
    return this.aircraftCategoryIds;
  }

  setAircraftCategoryIds(aircraftCategoryIds: Array<number>): OperatorPurchaseOffer {
    this.aircraftCategoryIds = aircraftCategoryIds;

    return this;
  }

  getAircraftCategories(): Array<AircraftCategory> {
    return this.aircraftCategories;
  }

  setAircraftCategories(categories: Array<AircraftCategory>): OperatorPurchaseOffer {
    this.aircraftCategories = categories;
    return this;
  }

  getAircraftCategoryNames(): string {
    if (this.aircraftCategories && this.aircraftCategories.length) {
      return this.aircraftCategories.map((category) => category.name).join(', ');
    } else {
      return null;
    }
  }

  getOfferPrice(): number {
    return this.offerPrice;
  }

  setOfferPrice(maxPrice: number): OperatorPurchaseOffer {
    this.offerPrice = maxPrice;
    return this;
  }

  getLegs(): Array<LegRequest> {
    return this.legs;
  }

  setLegs(legs: Array<LegRequest>): OperatorPurchaseOffer {
    this.legs = legs;
    return this;
  }

  getLastModifiedDate(): Date {
    return this.lastModifiedDate;
  }

  setLastModifiedDate(lastModifiedDate: Date): OperatorPurchaseOffer {
    this.lastModifiedDate = lastModifiedDate;
    return this;
  }

  getLastModifiedUser(): User {
    return this.lastModifiedUser;
  }

  setLastModifiedUser(lastModifiedUser: User): OperatorPurchaseOffer {
    this.lastModifiedUser = lastModifiedUser;
    return this;
  }

  getCreatedDate(): Date {
    return this.createdDate;
  }

  setCreatedDate(createdDate: Date): OperatorPurchaseOffer {
    this.createdDate = createdDate;
    return this;
  }

  getCreatedUser(): User {
    return this.createdUser;
  }

  setCreatedUser(createdUser: User): OperatorPurchaseOffer {
    this.createdUser = createdUser;
    return this;
  }

  getStatus(): Status {
    return this.status;
  }

  setStatus(status: Status): OperatorPurchaseOffer {
    this.status = status;
    return this;
  }

  getAircraftId(): number {
    return this.aircraftId;
  }

  setAircraftId(aircraftId: number): OperatorPurchaseOffer {
    this.aircraftId = aircraftId;
    return this;
  }

  getAcceptedDate(): Date {
    return this.acceptedDate;
  }

  setAcceptedDate(acceptedDate: Date): OperatorPurchaseOffer {
    this.acceptedDate = acceptedDate;
    return this;
  }

  getAcceptedByUser(): User {
    return this.acceptedByUser;
  }

  setAcceptedByUser(acceptedByUser: User): OperatorPurchaseOffer {
    this.acceptedByUser = acceptedByUser;
    return this;
  }

  getCancelledDate(): Date {
    return this.cancelledDate;
  }

  setCancelledDate(cancelledDate: Date): OperatorPurchaseOffer {
    this.cancelledDate = cancelledDate;
    return this;
  }

  getCancelledBy(): User {
    return this.cancelledBy;
  }

  setCancelledBy(cancelledBy: User): OperatorPurchaseOffer {
    this.cancelledBy = cancelledBy;
    return this;
  }

  getPriceType(): PriceTypeEnum {
    return this.priceType;
  }

  setPriceType(priceType: PriceTypeEnum): OperatorPurchaseOffer {
    this.priceType = priceType;
    return this;
  }

  getType(): OfferTypeEnum {
    return this.type;
  }

  setType(type: OfferTypeEnum): OperatorPurchaseOffer {
    this.type = type;
    return this;
  }

  getFlightPrice(): number {
    return this.flightPrice;
  }

  setFlightPrice(flightPrice: number): OperatorPurchaseOffer {
    this.flightPrice = flightPrice;
    return this;
  }

  getOfferStatus(): OfferBidStatusEnum {
    return this.offerStatus;
  }

  setOfferStatus(offerStatus: OfferBidStatusEnum): OperatorPurchaseOffer {
    this.offerStatus = offerStatus;
    return this;
  }

  getAircraft(): Aircraft {
    return this.aircraft;
  }

  setAircraft(aircraft: Aircraft): OperatorPurchaseOffer {
    this.aircraft = aircraft;
    return this;
  }

  getOfferPriceInCurrency(): number {
    return this.offerPriceInCurrency;
  }

  setOfferPriceInCurrency(price: number): OperatorPurchaseOffer {
    this.offerPriceInCurrency = price;
    return this;
  }

  getCurrency(): CurrencyEnum {
    return this.currency;
  }

  setCurrency(currency: CurrencyEnum): OperatorPurchaseOffer {
    this.currency = currency;
    return this;
  }

  setCurrencyUpdateTime(updateDate: Date): OperatorPurchaseOffer {
    this.currencyUpdateTime = updateDate;
    return this;
  }

  getCurrencyUpdateTime(): Date {
    return this.currencyUpdateTime;
  }

  getCurrencyRate(): number {
    return this.currencyRate;
  }

  setCurrencyRate(rate: number): OperatorPurchaseOffer {
    this.currencyRate = rate;
    return this;
  }

  getLegAirportChanges(): Array<LegAirportChanges> {
    return this.legAirportChanges;
  }

  setLegAirportChanges(legAirportChanges: Array<LegAirportChanges>): OperatorPurchaseOffer {
    this.legAirportChanges = legAirportChanges;
    return this;
  }

  getFirmFlexDepartureFee(): number {
    return this.firmFlexDepartureFee;
  }

  setFirmFlexDepartureFee(fee: number): OperatorPurchaseOffer {
    this.firmFlexDepartureFee = fee;
    return this;
  }

  getFirmFlexDeparture(): boolean {
    return this.firmFlexDeparture;
  }

  setFirmFlexDeparture(value: boolean): OperatorPurchaseOffer {
    this.firmFlexDeparture = value;
    return this;
  }

  getCreationType(): CreationTypeEnum {
    return this.creationType;
  }

  setCreationType(creationType: CreationTypeEnum): OperatorPurchaseOffer {
    this.creationType = creationType;

    return this;
  }
}
